import { useNostr } from "@nostrify/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useCurrentUser } from "./useCurrentUser";
import {
  ORDER_RELAYS,
  ORDER_STATUS_KIND,
  ORDER_STATUS_META,
  type OrderStatus,
} from "@/lib/cashcraft";

interface SetStatusArgs {
  orderId: string;
  status: OrderStatus;
  note?: string;
}

/** Publishes (or replaces) the operator's status for a given order. */
export function useSetOrderStatus() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, status, note }: SetStatusArgs) => {
      if (!user) throw new Error("Not logged in");

      const event = await user.signer.signEvent({
        kind: ORDER_STATUS_KIND,
        content: note ?? "",
        tags: [
          ["d", orderId],
          ["status", status],
          ["alt", `BCH Launch order status: ${ORDER_STATUS_META[status].label}`],
        ],
        created_at: Math.floor(Date.now() / 1000),
      });

      await Promise.allSettled([
        nostr.group(ORDER_RELAYS).event(event, {
          signal: AbortSignal.timeout(5000),
        }),
        nostr.event(event, { signal: AbortSignal.timeout(5000) }),
      ]);
      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["order-statuses"] });
    },
  });
}
